/**
 * Set a supplier's credentials on their behalf, instead of waiting for them to
 * accept an invitation email.
 *
 * Onboarding stalls most often because the invitation is sitting in a spam
 * folder, and resending it only sends it back to the same filter. Here the
 * customer agrees a password with their contact out of band, sets it, and the
 * supplier signs in directly — landing in Pending Due Diligence with their
 * questionnaires waiting, exactly as if they had clicked the link.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Copy, Eye, EyeOff, Loader2, ShieldCheck, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../lib/api';

interface ActivateSupplierDialogProps {
  /** Invitation being activated; null closes the dialog. */
  invitation: {
    id: number;
    invitee_email: string;
    invitee_company_name?: string;
    invitee_contact_name?: string;
  } | null;
  onClose: () => void;
  /** Called after a successful activation so the caller can refetch. */
  onActivated: () => void;
}

/** Characters chosen to avoid the pairs people misread when a password is
 *  passed on by phone or in a chat message: no O/0, l/1/I, or 5/S. */
const SAFE_UPPER = 'ABCDEFGHJKLMNPQRTUVWXYZ';
const SAFE_LOWER = 'abcdefghjkmnpqrtuvwxyz';
const SAFE_DIGITS = '23456789';
const SAFE_SYMBOLS = '!@#$%&*?';

function generatePassword(): string {
  const all = SAFE_UPPER + SAFE_LOWER + SAFE_DIGITS + SAFE_SYMBOLS;
  const pick = (set: string) => set[Math.floor(Math.random() * set.length)];
  // One of each class up front so it always clears Django's validators, then
  // fill to 16 and shuffle so the classes aren't in a predictable order.
  const chars = [pick(SAFE_UPPER), pick(SAFE_LOWER), pick(SAFE_DIGITS), pick(SAFE_SYMBOLS)];
  while (chars.length < 16) chars.push(pick(all));
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

export default function ActivateSupplierDialog({
  invitation,
  onClose,
  onActivated,
}: ActivateSupplierDialogProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPassword('');
    setConfirm('');
    setReveal(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleGenerate = () => {
    const generated = generatePassword();
    setPassword(generated);
    setConfirm(generated);
    // Generated passwords are meant to be passed on, so show them — hiding a
    // value the admin has to read out is a step with no purpose.
    setReveal(true);
    setError(null);
  };

  const handleCopy = async () => {
    if (!invitation) return;
    try {
      await navigator.clipboard.writeText(
        `Portal: ${window.location.origin}\nUsername: ${invitation.invitee_email}\nPassword: ${password}`
      );
      toast.success('Sign-in details copied');
    } catch {
      toast.error('Could not copy — select the password and copy it manually');
    }
  };

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < 8;
  const canSubmit = password.length >= 8 && password === confirm && !submitting;

  const handleSubmit = async () => {
    if (!invitation || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await onboardingAPI.activateWithCredentials(invitation.id, password);
      const created = res.data.questionnaires_created;
      toast.success(
        `${invitation.invitee_company_name || 'Supplier'} activated — moved to Pending Due Diligence`
          + (created ? ` with ${created} questionnaire${created !== 1 ? 's' : ''}` : '')
      );
      onActivated();
      handleClose();
    } catch (err: unknown) {
      // The backend distinguishes a weak password from an address that already
      // belongs to a real account; both need to reach the admin verbatim,
      // because the fix is different in each case.
      const response = (err as { response?: { data?: { error?: string; detail?: string } } }).response;
      setError(
        response?.data?.error
        || response?.data?.detail
        || 'Could not activate this supplier. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!invitation} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            Set Credentials &amp; Activate
          </DialogTitle>
          <DialogDescription>
            Give {invitation?.invitee_company_name || 'this supplier'} a password so they can
            sign in without the invitation email. They move straight to Pending Due Diligence
            with their questionnaires waiting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <p className="text-xs text-muted-foreground mb-2">
              This creates a portal login for the supplier:
            </p>
            <div className="flex justify-between gap-4">
              {/* "Username", not "signs in as" — the latter reads like the
                  admin is about to impersonate them, which is a different
                  feature living in User Management. */}
              <span className="text-muted-foreground">Username</span>
              <span className="font-medium break-all">{invitation?.invitee_email}</span>
            </div>
            {invitation?.invitee_contact_name && (
              <div className="flex justify-between gap-4 mt-1">
                <span className="text-muted-foreground">Contact</span>
                <span className="font-medium">{invitation.invitee_contact_name}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="activate-password">Password</Label>
              <Button type="button" variant="ghost" size="sm" onClick={handleGenerate}>
                <Wand2 className="h-3.5 w-3.5 mr-1" />Generate
              </Button>
            </div>
            <div className="flex gap-2">
              <Input
                id="activate-password"
                type={reveal ? 'text' : 'password'}
                value={password}
                autoComplete="new-password"
                onChange={(e) => { setPassword(e.target.value); setError(null); }}
                placeholder="At least 8 characters"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={reveal ? 'Hide password' : 'Show password'}
                onClick={() => setReveal(v => !v)}
              >
                {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Copy sign-in details"
                disabled={!password}
                onClick={handleCopy}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            {tooShort && (
              <p className="text-xs text-red-600">Must be at least 8 characters.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="activate-password-confirm">Confirm password</Label>
            <Input
              id="activate-password-confirm"
              type={reveal ? 'text' : 'password'}
              value={confirm}
              autoComplete="new-password"
              onChange={(e) => { setConfirm(e.target.value); setError(null); }}
            />
            {mismatch && (
              <p className="text-xs text-red-600">Passwords do not match.</p>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <p className="text-xs text-muted-foreground">
            Share these details with your contact directly. Ask them to change the
            password once they have signed in.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {submitting ? 'Activating…' : 'Activate Supplier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
